import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { messages } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ResponsiveList, type Column } from '@/components/common/responsive-list';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CompanyFilterSheet } from '@/features/companies/filter-sheet';

type TagRow = {
  id: string;
  name: string;
  colorToken: string | null;
  companyCount: number;
  ruleJson: Record<string, unknown> | null;
  autoApply: boolean;
  matchCount: number | null;
};

type EditState = {
  id?: string;
  name: string;
  autoApply: boolean;
  rule: Record<string, string>;
};

// 空でない条件だけ残す（保存する分類基準）
const cleanRule = (rule: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(rule).filter(([, v]) => v !== '' && v != null));

const ruleAsFilters = (rule: Record<string, unknown> | null): Record<string, string> =>
  rule ? Object.fromEntries(Object.entries(rule).map(([k, v]) => [k, String(v)])) : {};

/** S74 タグ（作成・色・分類基準による自動タグ付け） */
export const SettingsTagsPage = () => {
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState<EditState | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [nlText, setNlText] = useState('');
  const [nlMatched, setNlMatched] = useState<string[] | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TagRow | null>(null);
  const [applyTarget, setApplyTarget] = useState<TagRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: qk.tags,
    queryFn: () => api.get<SingleResponse<TagRow[]>>('/tags'),
  });
  const { data: assignees } = useQuery({
    queryKey: qk.assignableUsers,
    queryFn: () =>
      api.get<SingleResponse<{ id: string; name: string | null; email: string }[]>>('/users/assignable'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.tags });

  const save = useMutation({
    mutationFn: (input: EditState) => {
      const rule = cleanRule(input.rule);
      const body = {
        name: input.name,
        autoApply: input.autoApply,
        ruleJson: Object.keys(rule).length ? rule : null,
      };
      return input.id
        ? api.patch(`/tags/${input.id}`, body)
        : api.post('/tags', body);
    },
    onSuccess: () => {
      toast.success('タグを保存しました');
      invalidate();
      setEdit(null);
      setPreview(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/tags/${id}`),
    onSuccess: () => {
      toast.success('タグを削除しました');
      invalidate();
      setRemoveTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const apply = useMutation({
    mutationFn: (tag: TagRow) => api.post<SingleResponse<{ matched: number; added: number; removed: number }>>(
      `/tags/${tag.id}/apply`,
      { sync: tag.autoApply },
    ),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`適用しました（一致 ${d.matched} 社 / 付与 ${d.added} / 解除 ${d.removed}）`);
      invalidate();
      setApplyTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const previewRule = useMutation({
    mutationFn: (rule: Record<string, string>) =>
      api.post<SingleResponse<{ count: number }>>('/tags/preview', { filter: cleanRule(rule) }),
    onSuccess: (res) => setPreview(res.data.count),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const parseNl = useMutation({
    mutationFn: (text: string) =>
      api.post<SingleResponse<{ filter: Record<string, string>; matched: string[]; count: number | null }>>(
        '/tags/parse-rule',
        { text },
      ),
    onSuccess: (res) => {
      const d = res.data;
      if (!edit) return;
      if (d.matched.length === 0) {
        toast.error('条件を読み取れませんでした。表現を変えるか「条件を設定」から手動で指定してください');
        setNlMatched([]);
        return;
      }
      setEdit({ ...edit, rule: d.filter });
      setNlMatched(d.matched);
      setPreview(d.count);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const ruleKeyCount = edit ? Object.keys(cleanRule(edit.rule)).length : 0;

  const columns: Column<TagRow>[] = useMemo(
    () => [
      { key: 'name', header: 'タグ', render: (row) => <span className="font-semibold">{row.name}</span> },
      {
        key: 'companyCount',
        header: '付与数',
        width: '90px',
        render: (row) => <span className="tabular">{row.companyCount.toLocaleString()}</span>,
      },
      {
        key: 'rule',
        header: '分類基準',
        render: (row) =>
          row.ruleJson ? (
            <span className="text-note">
              条件あり{row.matchCount != null && `・${row.matchCount.toLocaleString()} 社一致`}
            </span>
          ) : (
            <span className="text-note text-foreground-subtle">未設定</span>
          ),
      },
      {
        key: 'autoApply',
        header: '自動',
        width: '80px',
        render: (row) =>
          row.autoApply ? <Badge variant="solid">オン</Badge> : <span className="text-note text-foreground-subtle">—</span>,
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.tags}
        badge={data?.data ? <Badge variant="muted">{data.data.length} 件</Badge> : undefined}
        actions={
          <Button onClick={() => { setEdit({ name: '', autoApply: false, rule: {} }); setPreview(null); setNlText(''); setNlMatched(null); }}>
            <Plus aria-hidden />
            タグを追加
          </Button>
        }
      />

      <p className="mb-3 text-note text-foreground-muted">
        分類基準（企業の絞り込み条件）を設定すると、一致する企業へタグを付与できます。「自動」をオンにしたタグは、
        「今すぐ適用」で基準に一致しなくなった企業からも外れます（基準が正）。
      </p>

      <ResponsiveList
        columns={columns}
        rows={data?.data ?? []}
        isLoading={isLoading}
        rowActions={(row) => (
          <span className="flex items-center gap-1">
            {row.ruleJson && (
              <Button variant="ghost" size="sm" onClick={() => setApplyTarget(row)}>
                今すぐ適用
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEdit({ id: row.id, name: row.name, autoApply: row.autoApply, rule: ruleAsFilters(row.ruleJson) });
                setPreview(row.matchCount);
                setNlText(''); setNlMatched(null);
              }}
            >
              編集
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(row)}>
              削除
            </Button>
          </span>
        )}
        empty={<EmptyState title="タグがありません" />}
        card={(row) => (
          <div className="space-y-1.5">
            <p className="flex flex-wrap items-center gap-2">
              <span className="text-body font-semibold">{row.name}</span>
              {row.autoApply && <Badge variant="solid">自動</Badge>}
            </p>
            <p className="text-note text-foreground-muted">
              付与 {row.companyCount} 社
              {row.ruleJson ? `・基準あり${row.matchCount != null ? `（${row.matchCount} 社一致）` : ''}` : '・基準なし'}
            </p>
            <span className="flex gap-1">
              {row.ruleJson && (
                <Button variant="outline" size="sm" onClick={() => setApplyTarget(row)}>
                  今すぐ適用
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEdit({ id: row.id, name: row.name, autoApply: row.autoApply, rule: ruleAsFilters(row.ruleJson) });
                  setPreview(row.matchCount);
                }}
              >
                編集
              </Button>
              <Button variant="outline" size="sm" onClick={() => setRemoveTarget(row)}>
                削除
              </Button>
            </span>
          </div>
        )}
      />

      {/* 作成・編集ダイアログ */}
      <Dialog open={edit != null} onOpenChange={(o) => { if (!o) { setEdit(null); setPreview(null); setNlText(''); setNlMatched(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit?.id ? 'タグを編集' : 'タグを追加'}</DialogTitle>
          </DialogHeader>
          {edit && (
            <DialogBody className="space-y-4">
              <Field label="タグ名">
                {(props) => (
                  <Input
                    {...props}
                    value={edit.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    placeholder="例：EC・DMARC未設定"
                    maxLength={40}
                  />
                )}
              </Field>

              <div className="rounded-md border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-note font-medium">分類基準（自動タグ付けの条件）</span>
                  <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)}>
                    <SlidersHorizontal aria-hidden />
                    条件を設定
                  </Button>
                </div>

                {/* 自然言語で条件を入力（キーワード解釈） */}
                <div className="mt-2 flex gap-2">
                  <Input
                    value={nlText}
                    onChange={(e) => setNlText(e.target.value)}
                    placeholder="例：東京のEC企業でセキュリティリスク高、メールあり"
                    onKeyDown={(e) => { if (e.key === 'Enter' && nlText.trim()) parseNl.mutate(nlText.trim()); }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => nlText.trim() && parseNl.mutate(nlText.trim())}
                    disabled={parseNl.isPending || !nlText.trim()}
                  >
                    解釈
                  </Button>
                </div>
                {nlMatched != null && nlMatched.length > 0 && (
                  <p className="mt-1 text-note text-foreground-muted">読み取った条件：{nlMatched.join(' / ')}</p>
                )}
                <p className="mt-1 text-note text-foreground-subtle">
                  対応：セキュリティリスク・診断有無・業種・都道府県・連絡先(メール/フォーム/電話)有無・返信・状況・既存タグ。DMARC 等の診断詳細は対象外です。
                </p>
                <p className="mt-2 text-note text-foreground-muted">
                  {ruleKeyCount > 0 ? `条件 ${ruleKeyCount} 件を設定中` : '未設定（条件なしのタグは手動で付与します）'}
                </p>
                {ruleKeyCount > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => previewRule.mutate(edit.rule)}
                      disabled={previewRule.isPending}
                    >
                      一致数を確認
                    </Button>
                    {preview != null && <span className="text-note">→ {preview.toLocaleString()} 社が一致</span>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEdit({ ...edit, rule: {} }); setPreview(null); }}
                    >
                      基準を外す
                    </Button>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-note">
                <input
                  type="checkbox"
                  checked={edit.autoApply}
                  onChange={(e) => setEdit({ ...edit, autoApply: e.target.checked })}
                />
                自動で維持する（基準に一致しなくなった企業からタグを外す）
              </label>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEdit(null); setPreview(null); setNlText(''); setNlMatched(null); }}>
              キャンセル
            </Button>
            <Button
              onClick={() => edit && save.mutate(edit)}
              disabled={save.isPending || !edit?.name.trim()}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 分類基準の条件を編集（企業一覧のフィルタを流用） */}
      {edit && (
        <CompanyFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          filters={edit.rule}
          onChange={(key, value) => { setEdit({ ...edit, rule: { ...edit.rule, [key]: value } }); setPreview(null); }}
          onReset={() => { setEdit({ ...edit, rule: {} }); setPreview(null); }}
          assignees={assignees?.data ?? []}
          tags={(data?.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
        />
      )}

      <ConfirmDialog
        open={applyTarget != null}
        onOpenChange={(o) => { if (!o) setApplyTarget(null); }}
        title="分類基準を適用しますか？"
        summary={
          applyTarget?.autoApply
            ? '基準に一致する企業へタグを付与し、一致しなくなった企業からは外します。'
            : '基準に一致する企業へタグを付与します（既存の付与は外しません）。'
        }
        confirmText="適用する"
        onConfirm={() => applyTarget && apply.mutate(applyTarget)}
        isPending={apply.isPending}
      />

      <ConfirmDialog
        open={removeTarget != null}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title="タグを削除しますか？"
        summary={`「${removeTarget?.name}」を削除します。企業への付与も外れます。`}
        confirmText="削除する"
        irreversible
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        isPending={remove.isPending}
      />
    </>
  );
};
